import type {FetchResult} from '@apollo/client';
import {useCallback} from 'react';

import {type CreateProductMutationResult} from 'web-app/api/clients/rest-types/create-product/create-product';
import {type CreateSessionPackageMutationResult} from 'web-app/api/clients/rest-types/create-session-package/create-session-package';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import {isNonEmpty} from '@famly/stat_ts-utils_non-empty-array';
import type * as Warnable from 'web-app/util/warnable';

import type {ApiCreatedProduct} from '../api-types';
import {transformApiSessionToTuition} from '../transformers/api-transformers';
import type * as Types from '../types';
import type {SubmissionData} from './use-submission-data';
import {useSubmitTuition} from './use-submit-tuition';
import {useSubmitFee} from './use-submit-fee';

const isProductCreationResult = (
    result: CreateProductMutationResult | CreateSessionPackageMutationResult,
): result is CreateProductMutationResult => {
    return 'createProduct' in result;
};

export const useCreateProratedItems = () => {
    const {submit: submitSessionPackage, isSessionPackageCreationLoading} = useSubmitTuition();
    const {submit: submitProduct, isProductCreationLoading} = useSubmitFee();

    const createProratedItems = useCallback(
        async ({
            proratedProducts,
            proratedSessions,
            billingProfiles,
        }: {
            proratedProducts: NonNullable<SubmissionData['customStartPeriod']>['customStartPeriodProratedProducts'];
            proratedSessions: NonNullable<SubmissionData['customStartPeriod']>['customStartPeriodProratedSessions'];
            billingProfiles: Array<Types.BillingProfile>;
        }) => {
            const productCreationPromises = proratedProducts.map(product => submitProduct(product));

            const sessionPackageCreationPromises = proratedSessions.map(sessionPackage =>
                submitSessionPackage(sessionPackage),
            );

            const results = await Promise.all([...productCreationPromises, ...sessionPackageCreationPromises]);
            const feesAndTuitions = transformResults(results, billingProfiles);

            return getProratedTuitionsAndFees(feesAndTuitions);
        },
        [submitProduct, submitSessionPackage],
    );

    return {createProratedItems, isLoading: isProductCreationLoading || isSessionPackageCreationLoading};
};

export const transformResults = (
    results: Array<FetchResult<CreateProductMutationResult> | FetchResult<CreateSessionPackageMutationResult>>,
    billingProfiles: Array<Types.BillingProfile>,
) => {
    return results
        .map(r => {
            const result = r.data;
            if (!result) {
                return null;
            }

            return isProductCreationResult(result)
                ? {
                      productId: result.createProduct.product.id,
                      availabilityIdsByDays: result.createProduct.product.availabilityIdsByDays,
                      pricing: result.createProduct.product.pricing,
                  }
                : {
                      tuition: transformApiSessionToTuition({
                          billingProfiles,
                          hiddenTuitionsToDisplay: [result.createSessionPackage.session.id],
                      })(result.createSessionPackage.session),
                  };
        })
        .filter(hasValue);
};

const getProratedTuitionsAndFees = (
    proratedItems: ReturnType<typeof transformResults>,
): null | {
    proratedTuitions: NonEmptyArray<Warnable.Warnable<null | Types.Tuition, string>>;
    proratedFees: NonEmptyArray<ApiCreatedProduct>;
} => {
    const proratedTuitions = proratedItems.map(sessionPackage => sessionPackage.tuition ?? null).filter(hasValue);
    const proratedFees = proratedItems
        .map(product => {
            if (!product.productId || !product.availabilityIdsByDays || !product.pricing) {
                return null;
            }
            return {
                id: product.productId,
                availabilityIdsByDays: product.availabilityIdsByDays,
                pricing: product.pricing,
                discountable: false,
                title: '',
                hiddenByUser: true,
            };
        })
        .filter(hasValue);

    if (isNonEmpty(proratedTuitions) && isNonEmpty(proratedFees)) {
        return {proratedTuitions, proratedFees};
    }
    return null;
};
